// src/components/home/homeSections.js
//
// Pure helpers behind the restructured home page (home-4a-hero). No DOM, no React:
// the two decisions that shape the page -- which presenter the hero shows, and how
// the admin-editable features split across two sections -- are data, so they are
// unit-tested rather than eyeballed.
//
// Nothing here invents copy. Every string still comes from Site Text; these helpers
// only choose between values that already exist.

// The hero's presenter block has three states, and exactly one is always chosen --
// the hero is never left with an empty slot or a placeholder:
//
//   video       -- homeIntroVideoUrl is set AND parseable
//   photo       -- else profilePhotoUrl is set
//   credentials -- else the four CREDENTIAL_TILES in one compact row
//
// The video gate is a PARSEABLE id, not merely a non-blank string: a typo'd URL must
// fall through to the photo rather than render an empty 16:9 frame. That is the whole
// reason this is a function and not an inline ternary.
//
// `parseVideo` is INJECTED (pass parseYouTubeId in the app) because it lives in a .jsx
// module node:test cannot parse -- the same pattern selectRenderableParts already uses
// in techniqueParts.js.
export function presenterState(siteText, parseVideo) {
    const st = siteText || {};
    const parse = typeof parseVideo === 'function' ? parseVideo : () => null;
    const videoUrl = typeof st.homeIntroVideoUrl === 'string' ? st.homeIntroVideoUrl.trim() : '';
    const photoUrl = (Array.isArray(st.heroGallery) ? st.heroGallery : []).find(p => typeof p?.imageUrl === 'string' && p.imageUrl.trim())?.imageUrl.trim() || '';
    if (videoUrl && parse(videoUrl)) return { kind: 'video', videoUrl, photoUrl };
    if (photoUrl) return { kind: 'photo', videoUrl: '', photoUrl };
    return { kind: 'credentials', videoUrl: '', photoUrl: '' };
}

// How many features lead the page as the "what changes" strip.
export const LEAD_FEATURE_COUNT = 3;

// A feature entry carries no icon of its own -- the icon comes from FEATURE_ICON_KEYS
// BY POSITION. So the split has to carry the ORIGINAL index with each row, or moving
// the first three into their own section would silently re-assign every icon.
//
// Renders what exists: a shorter features list simply produces a shorter lead strip
// (and possibly an empty rest), never a padded one.
export function splitFeatures(features, leadCount = LEAD_FEATURE_COUNT) {
    const list = (Array.isArray(features) ? features : [])
        .map((feat, index) => ({ feat, index }))
        .filter((e) => e.feat && (e.feat.title || e.feat.text));
    const n = Math.max(0, Math.floor(Number(leadCount) || 0));
    return { lead: list.slice(0, n), rest: list.slice(n) };
}

// Photos / testimonials render ONLY when the admin enabled them AND there is real
// content -- the same gate the page has always applied, kept in one tested place so a
// restyle cannot loosen it by accident. Never returns rows for a disabled carousel.
export function visiblePhotos(siteText) {
    const st = siteText || {};
    if (!st.photosCarouselEnabled) return [];
    return (Array.isArray(st.photos) ? st.photos : [])
        .filter((p) => p && typeof p.imageUrl === 'string' && p.imageUrl.trim());
}

export function visibleTestimonials(siteText) {
    const st = siteText || {};
    if (!st.testimonialsEnabled) return [];
    return (Array.isArray(st.testimonials) ? st.testimonials : [])
        .filter((t) => t && typeof t.quote === 'string' && t.quote.trim());
}
